// src/features/files/hooks/useFolders.js
import { useState, useCallback } from 'react';
import { foldersAPI } from '../services/folders';
import { useApi } from '../../../hooks/api/useApi';

export function useFolders() {
  const { loading, error, execute, clearError } = useApi();
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderContents, setFolderContents] = useState(null);
  const [folderTree, setFolderTree] = useState(null);

  // Create folder
  const createFolder = useCallback(async (folderData) => {
    const result = await execute(() => foldersAPI.createFolder(folderData));
    return result;
  }, [execute]);

  // Get folder details
  const getFolderDetails = useCallback(async (folderId) => {
    const result = await execute(() => foldersAPI.getFolderDetails(folderId));
    if (result) {
      setCurrentFolder(result.data);
    }
    return result;
  }, [execute]);

  // Get root contents (folders + files at root)
  const getRootContents = useCallback(async () => {
    const result = await execute(() => foldersAPI.getRootContents());
    if (result) {
      setFolderContents(result.data);
    }
    return result;
  }, [execute]);

  // List subfolders
  const listSubfolders = useCallback(async (folderId) => {
    const result = await execute(() => foldersAPI.listSubfolders(folderId));
    return result;
  }, [execute]);

  // Get folder tree
  const getFolderTree = useCallback(async (rootFolderId = null) => {
    const result = await execute(() => foldersAPI.getFolderTree(rootFolderId));
    if (result) {
      setFolderTree(result.data);
    }
    return result;
  }, [execute]);

  // Get folder contents
  const getFolderContents = useCallback(async (folderId) => {
    const result = await execute(() => foldersAPI.getFolderContents(folderId));
    if (result) {
      setFolderContents(result.data);
    }
    return result;
  }, [execute]);

  // Update folder
  const updateFolder = useCallback(async (folderId, updateData) => {
    const result = await execute(() => foldersAPI.updateFolder(folderId, updateData));
    if (result && currentFolder?.folder_id === folderId) {
      setCurrentFolder({ ...currentFolder, ...updateData });
    }
    return result;
  }, [execute, currentFolder]);

  // Move folder
  const moveFolder = useCallback(async (folderId, newParentFolderId) => {
    const result = await execute(() => foldersAPI.moveFolder(folderId, newParentFolderId));
    return result;
  }, [execute]);

  // Delete folder
  const deleteFolder = useCallback(async (folderId, options = {}) => {
    const result = await execute(() => foldersAPI.deleteFolder(folderId, options));
    if (result && currentFolder?.folder_id === folderId) {
      setCurrentFolder(null);
    }
    return result;
  }, [execute, currentFolder]);

  // Navigate to folder (updates contents)
  const navigateToFolder = useCallback(async (folderId) => {
    if (folderId === null) {
      // Navigate to root
      return await getRootContents();
    } else {
      // Navigate to specific folder
      return await getFolderContents(folderId);
    }
  }, [getRootContents, getFolderContents]);

  // Clear current folder state
  const clearCurrentFolder = useCallback(() => {
    setCurrentFolder(null);
    setFolderContents(null);
  }, []);

  return {
    loading,
    error,
    clearError,
    currentFolder,
    folderContents,
    folderTree,
    // Actions
    createFolder,
    getFolderDetails,
    getRootContents,
    listSubfolders,
    getFolderTree,
    getFolderContents,
    updateFolder,
    moveFolder,
    deleteFolder,
    navigateToFolder,
    clearCurrentFolder,
  };
}